import { useEffect, useState } from "react";
import { api, TYPE_LABELS, wholesaleBadge, money, productImage } from "@/lib/api";
import { toast } from "sonner";
import { Loader2, Boxes, Plus, ArrowRight, X, ShoppingCart, Trash2 } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";

const STATUS_LABEL = { pending: "Pending", accepted: "Accepted", packing: "Packing", shipped: "Shipped" };
const NEXT = { pending: "accepted", accepted: "packing", packing: "shipped" };
const NEXT_LABEL = { pending: "Accept order", accepted: "Start packing", packing: "Mark shipped" };

function CreateModal({ onClose, onCreated }) {
  const [products, setProducts] = useState([]);
  const [buyer, setBuyer] = useState("");
  const [items, setItems] = useState({});
  const [orderMin, setOrderMin] = useState(100);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    api.get("/products").then(({ data }) => setProducts(data));
    api.get("/channels").then(({ data }) => setOrderMin(data.settings?.faire?.order_minimum ?? 100));
  }, []);

  const setQty = (id, v) => setItems((m) => ({ ...m, [id]: v }));
  const lines = products.filter((p) => Number(items[p.id]) > 0).map((p) => ({ p, qty: Number(items[p.id]) }));
  const subtotal = lines.reduce((s, { p, qty }) => s + (p.wholesale_price || 0) * qty, 0);
  const belowMoq = lines.filter(({ p, qty }) => qty < (p.moq || 6));
  const valid = lines.length > 0 && subtotal >= orderMin && belowMoq.length === 0 && buyer.trim();

  const submit = async () => {
    setSubmitting(true);
    try {
      await api.post("/orders/wholesale", { buyer_name: buyer, channel: "faire", line_items: lines.map(({ p, qty }) => ({ product_id: p.id, qty })) });
      toast.success("Wholesale order created");
      onCreated();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not create order"); }
    finally { setSubmitting(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" data-testid="wholesale-modal">
      <div className="bg-white rounded-xl w-full max-w-2xl max-h-[85vh] overflow-hidden flex flex-col">
        <div className="flex items-center justify-between p-5 border-b border-zinc-200">
          <h2 className="font-display text-lg font-bold flex items-center gap-2"><ShoppingCart className="w-5 h-5" /> New wholesale order</h2>
          <button onClick={onClose} data-testid="close-modal-btn" className="text-zinc-400 hover:text-zinc-700"><X className="w-5 h-5" /></button>
        </div>
        <div className="p-5 overflow-y-auto space-y-4">
          <label className="block">
            <span className="text-xs text-zinc-500">Retailer / buyer name</span>
            <Input value={buyer} onChange={(e) => setBuyer(e.target.value)} placeholder="e.g. Willow & Birch Boutique" className="mt-1" data-testid="buyer-input" />
          </label>
          <div>
            <div className="overline text-zinc-400 mb-2">Products &amp; quantities</div>
            {products.length === 0 ? <p className="text-sm text-zinc-400">No products available. Build products first.</p> : (
              <div className="space-y-2">
                {products.map((p) => {
                  const qty = Number(items[p.id] || 0);
                  const below = qty > 0 && qty < (p.moq || 6);
                  return (
                    <div key={p.id} className="flex items-center gap-3 p-2 rounded-lg border border-zinc-100">
                      <img src={productImage(p)} alt="" className="w-10 h-10 rounded object-cover bg-zinc-100" />
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium truncate">{p.listing_title}</div>
                        <div className="text-[11px] text-zinc-400">{money(p.wholesale_price || 0)}/unit · MOQ {p.moq || 6}</div>
                      </div>
                      <Input type="number" min="0" value={items[p.id] || ""} onChange={(e) => setQty(p.id, e.target.value)} placeholder="0" className={`w-20 h-9 text-sm ${below ? "border-rose-400" : ""}`} data-testid={`qty-${p.id}`} />
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
        <div className="p-5 border-t border-zinc-200 bg-zinc-50">
          <div className="flex items-center justify-between text-sm mb-2">
            <span className="text-zinc-500">Subtotal {subtotal < orderMin && <span className="text-rose-500">(min {money(orderMin)})</span>}</span>
            <span className="font-bold text-lg" data-testid="modal-subtotal">{money(subtotal)}</span>
          </div>
          {belowMoq.length > 0 && <p className="text-xs text-rose-500 mb-2">Some items are below their minimum order quantity.</p>}
          <button onClick={submit} disabled={!valid || submitting} data-testid="submit-wholesale-btn" className="w-full h-11 rounded-md bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 text-white font-medium flex items-center justify-center gap-2">
            {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />} Create order
          </button>
        </div>
      </div>
    </div>
  );
}

export default function Wholesale() {
  const [orders, setOrders] = useState([]);
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);

  const load = () => api.get("/orders?order_type=wholesale").then(({ data }) => {
    setOrders(data);
    setLoading(false);
    setSelected((s) => (s ? data.find((o) => o.id === s.id) || data[0] : data[0]));
  });
  useEffect(() => { load(); }, []);

  const advance = async (o) => {
    const next = NEXT[o.status];
    if (!next) return;
    const { data } = await api.patch(`/orders/${o.id}`, { status: next });
    setOrders((prev) => prev.map((x) => (x.id === data.id ? data : x)));
    setSelected(data);
    toast.success(`Order ${STATUS_LABEL[next]}`);
  };

  const remove = async (o) => {
    if (!window.confirm(`Delete wholesale order ${o.order_number} (${o.buyer_name})? This can't be undone. Real Printify orders are never affected.`)) return;
    try {
      await api.delete(`/orders/${o.id}`);
      toast.success("Order deleted");
      setSelected(null);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not delete order"); }
  };

  if (loading) return <div className="flex justify-center py-24"><Loader2 className="w-6 h-6 animate-spin text-blue-600" /></div>;

  return (
    <div className="max-w-[1400px] mx-auto" data-testid="wholesale-page">
      <div className="mb-5 flex items-end justify-between flex-wrap gap-3">
        <div>
          <p className="overline text-blue-600">Faire · Wholesale</p>
          <h1 className="font-display text-3xl font-bold tracking-tight mt-1">Wholesale orders</h1>
          <p className="text-sm text-zinc-500 mt-1">Bulk orders from retailers at wholesale pricing, with MOQ &amp; order-minimum enforcement.</p>
        </div>
        <button onClick={() => setCreating(true)} data-testid="new-wholesale-btn" className="h-10 px-4 rounded-md bg-zinc-900 hover:bg-zinc-800 text-white text-sm font-medium flex items-center gap-1.5"><Plus className="w-4 h-4" /> New wholesale order</button>
      </div>

      <div className="grid lg:grid-cols-[380px_1fr] gap-5">
        <div className="space-y-2">
          {orders.length === 0 ? (
            <div className="border border-dashed border-zinc-300 rounded-lg p-12 text-center"><Boxes className="w-8 h-8 text-zinc-300 mx-auto" /><p className="text-sm text-zinc-500 mt-3">No wholesale orders yet.</p></div>
          ) : orders.map((o) => (
            <button key={o.id} onClick={() => setSelected(o)} data-testid={`wholesale-row-${o.id}`} className={`w-full text-left p-3 rounded-lg border transition-all ${selected?.id === o.id ? "border-blue-500 bg-blue-50/50 ring-1 ring-blue-500" : "border-zinc-200 bg-white hover:border-zinc-300"}`}>
              <div className="flex items-center justify-between"><span className="text-xs font-mono text-zinc-400">{o.order_number}</span><Badge className={`text-[9px] border ${wholesaleBadge[o.status]}`}>{STATUS_LABEL[o.status]}</Badge></div>
              <div className="text-sm font-semibold mt-1">{o.buyer_name}</div>
              <div className="flex items-center justify-between mt-1 text-xs text-zinc-500"><span>{o.units_total} units · {(o.line_items || []).length} SKUs</span><span className="font-bold text-emerald-600">{money(o.subtotal)}</span></div>
            </button>
          ))}
        </div>

        {selected ? (
          <div className="bg-white border border-zinc-200 rounded-lg p-5" data-testid="wholesale-detail">
            <div className="flex items-start justify-between">
              <div><span className="text-xs font-mono text-zinc-400">{selected.order_number} · {selected.channel}</span><h2 className="font-display text-lg font-bold tracking-tight mt-0.5">{selected.buyer_name}</h2></div>
              <Badge className={`text-[10px] border ${wholesaleBadge[selected.status]}`}>{STATUS_LABEL[selected.status]}</Badge>
            </div>
            <div className="mt-4 space-y-2">
              {(selected.line_items || []).map((li, i) => (
                <div key={i} className="flex items-center gap-3 p-2.5 rounded-lg border border-zinc-100 bg-zinc-50">
                  <img src={productImage(li)} alt="" className="w-10 h-10 rounded object-cover bg-zinc-100" />
                  <div className="flex-1 min-w-0"><div className="text-sm font-medium truncate">{li.product_title}</div><div className="text-[11px] text-zinc-400">{TYPE_LABELS[li.product_type]} · {money(li.unit_price)}/unit · MOQ {li.moq}</div></div>
                  <div className="text-right"><div className="text-sm font-semibold">×{li.qty}</div><div className="text-[11px] text-emerald-600 font-medium">{money(li.line_total)}</div></div>
                </div>
              ))}
            </div>
            <div className="mt-4 flex items-center justify-between border-t border-zinc-100 pt-3">
              <div className="text-sm text-zinc-500">{selected.units_total} units total</div>
              <div className="text-lg font-bold" data-testid="detail-subtotal">{money(selected.subtotal)}</div>
            </div>
            {selected.status !== "shipped" && (
              <button onClick={() => advance(selected)} data-testid="advance-wholesale-btn" className="mt-4 h-10 px-4 rounded-md bg-zinc-900 hover:bg-zinc-800 text-white text-sm font-medium flex items-center gap-2">{NEXT_LABEL[selected.status]} <ArrowRight className="w-4 h-4" /></button>
            )}
            <button onClick={() => remove(selected)} data-testid="delete-wholesale-btn" className="mt-4 ml-2 h-10 px-4 rounded-md border border-rose-200 text-rose-600 hover:bg-rose-50 text-sm font-medium inline-flex items-center gap-2"><Trash2 className="w-4 h-4" /> Delete order</button>
          </div>
        ) : (
          <div className="border border-dashed border-zinc-300 rounded-lg flex flex-col items-center justify-center py-24"><Boxes className="w-10 h-10 text-zinc-300" /><p className="text-sm text-zinc-500 mt-3">Select or create a wholesale order.</p></div>
        )}
      </div>

      {creating && <CreateModal onClose={() => setCreating(false)} onCreated={() => { setCreating(false); load(); }} />}
    </div>
  );
}
